<template>
  <div class="form-wrapper">
    <div class="form-group">
      <label for="rowName" class="text-start">{{
        $t('saved_rows.name')
      }}</label>
      <input
        type="text"
        class="form-control input-wide"
        :placeholder="$t('saved_rows.name')"
        id="rowName"
        v-model="rowName"
      />
      <p v-if="rowNameEmpty" class="error" data-test="empty_name_error">
        {{ $t('saved_rows.name_empty') }}
      </p>
    </div>

    <div class="form-group">
      <label for="category-select" class="text-start">{{
        $t('saved_rows.category')
      }}</label>

      <Multiselect
        id="category-select"
        v-model="rowCategoryInternal"
        :options="availableCategories"
        :searchable="true"
        :taggable="true"
        :multiple="true"
        :max="1"
        label="name"
        track-by="id"
        :placeholder="$t('row_categories.name')"
        :selectLabel="$t('row_categories.select_label')"
        :selectedLabel="$t('row_categories.selected_label')"
        :deselectLabel="$t('row_categories.deselect_label')"
        :tag-placeholder="$t('row_categories.create_category_label')"
        @tag="addCategory"
      >
        <template #option="{option, search}">
          <div class="custom-option">
            <span>{{ getOptionLabel(option) }}</span>
            <span v-if="isNewOption(option, search)" class="hint-text">
              {{ $t('saved_rows.press_enter_to_create') }}
            </span>
          </div>
        </template>

        <template #noOptions>
          <div class="no-options">
            {{ $t('saved_rows.no_categories_found') }}
          </div>
        </template>

        <template #maxElements>
          {{ $t('saved_rows.only_one_category') }}
        </template>
      </Multiselect>

      <p v-if="rowCategoryEmpty" class="error">
        {{ $t('saved_rows.category_empty') }}
      </p>
    </div>

    <div class="form-actions">
      <div></div>
      <div class="right-buttons">
        <button
          v-if="!editRowExternalDialog"
          id="save-as-new-btn"
          class="btn btn-primary"
          @click="persistRow(true)"
        >
          {{ $t('saved_rows.save_as_new') }}
        </button>
        <button
          v-if="saveRowData && saveRowData.metadata?.id"
          id="update-btn"
          class="btn btn-primary"
          @click="persistRow(false)"
        >
          {{ $t('saved_rows.update') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions} from 'vuex';
import Multiselect from 'vue-multiselect';

export default {
  name: 'SaveRowForm',
  components: {
    Multiselect
  },
  computed: {
    ...mapState({
      rowCategories: state => state.rowCategoriesModule.rowCategories,
      error: state => state.error,
      saveRowPromise: state => state.saveRowPromise,
      saveRowData: state => state.saveRowData,
      editRowExternalDialog: state => state.editRowExternalDialog
    })
  },
  methods: {
    ...mapActions('rowCategoriesModule', ['saveRowCategory']),
    ...mapActions('rowsModule', ['saveRow']),
    validateInputs() {
      this.rowNameEmpty = !this.rowName || this.rowName.trim() === '';
      this.rowCategoryEmpty = !this.rowCategory;

      return !this.rowNameEmpty && !this.rowCategoryEmpty;
    },
    async persistRow(saveAsNew) {
      if (!this.validateInputs()) return;

      if (this.rowCategory && !this.rowCategory.id) {
        const categoryResponse = await this.saveRowCategory(this.rowCategory);
        if (!this.error) {
          this.rowCategory = categoryResponse;
          this.rowCategories.push(this.rowCategory);
          this.$emit('new-category');
        }
      }

      const metadata = {
        name: this.rowName,
        category: this.rowCategory.id
      };
      if (!saveAsNew && this.saveRowData.metadata.id)
        metadata.id = this.saveRowData.metadata.id;

      const saveRowRequest = {
        ...this.saveRowData,
        metadata: {...metadata}
      };

      try {
        const rowResponse = await this.saveRow({
          row: saveRowRequest,
          saveAsNew
        });
        const newMetadata = JSON.parse(rowResponse.data.beeContent).metadata;
        // Since we're reusing this callback for both `onSaveRow` and `onEditRow`
        // the `saveRowPromise` refers to two different promises:
        // - when editing a row it expects a simple true/false argument indicating
        //   the editor should refresh the list of saved rows
        // - when saving a row it expects the metadata for the row we just saved
        if (this.editRowExternalDialog) {
          this.saveRowPromise.resolve(true);
        } else {
          this.saveRowPromise.resolve(newMetadata);
        }
        this.$emit('row-saved');
      } catch (error) {
        this.saveRowPromise.reject(error);
      }
    },
    addCategory(name) {
      const trimmed = name.trim();
      const exists = this.availableCategories.some(
        cat => cat.name.toLowerCase() === trimmed.toLowerCase()
      );

      if (exists || trimmed.length === 0) return;

      const newCategory = {
        name: trimmed
      };

      this.availableCategories.push(newCategory);
      this.rowCategoryInternal = [newCategory];
    },
    onCategorySearch(search) {
      this.search = search;
    },
    getOptionLabel(option) {
      return typeof option === 'string' ? option : option.label || option.name;
    },
    isNewOption(option, search) {
      if (!search) return false;
      const name = typeof option === 'string' ? option : option.name;
      return name?.toLowerCase() === search.toLowerCase();
    },
    resetForm() {
      this.rowName = '';
      this.rowNameEmpty = false;
      this.rowCategory = null;
      this.rowCategoryEmpty = false;
      this.rowCategoryInternal = [];
      this.availableCategories = [];
    }
  },
  watch: {
    saveRowData: {
      immediate: true,
      handler(newVal) {
        if (
          newVal &&
          newVal.metadata &&
          newVal.metadata.name &&
          newVal.metadata.category
        ) {
          this.rowName = newVal.metadata.name;

          const matchedCategory = this.availableCategories.find(
            cat => cat.id === newVal.metadata.category
          );
          if (matchedCategory) {
            this.rowCategoryInternal = [matchedCategory];
          }
        }
      }
    },
    rowCategories: {
      immediate: true,
      handler(newVal) {
        this.availableCategories = Array.isArray(newVal) ? [...newVal] : [];
      }
    },
    rowCategoryInternal: {
      immediate: true,
      handler(newVal) {
        this.rowCategory = newVal.length > 0 ? newVal[0] : null;
      }
    }
  },
  data: function () {
    return {
      rowName: '',
      rowNameEmpty: false,
      rowCategory: null,
      rowCategoryInternal: [],
      rowCategoryEmpty: false,
      availableCategories: []
    };
  }
};
</script>

<style scoped>
.form-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.form-group {
  width: 100%;
  max-width: 500px;
  margin-bottom: 20px;
  text-align: left;
}

.input-wide {
  width: 100%;
}

::v-deep(.multiselect__content-wrapper) {
  max-width: 500px;
  width: 100%;
  box-sizing: border-box;
}

::v-deep(.multiselect) {
  max-width: 500px;
  width: 100%;
  box-sizing: border-box;
}

.error {
  color: red;
  margin-top: 4px;
}

.custom-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  padding: 5px 10px;
}

.hint-text {
  font-size: 12px;
  color: #999;
  font-style: italic;
  margin-left: 8px;
}

.no-options {
  padding: 8px 10px;
  font-style: italic;
  color: #ccc;
}

.spaced {
  margin-right: 1em;
}

.error {
  color: red;
}

::v-deep(.vs__selected) {
  display: flex;
  align-items: center;
  height: 100%;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  line-height: normal;
}

::v-deep(.vs__search) {
  font-size: 14px;
}

::v-deep(.vs__dropdown-toggle) {
  height: 33px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 0 0.75rem;
  background-color: #fff;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
  display: flex;
  align-items: center;
}

::v-deep(.vs__selected-options) {
  padding: 0 !important;
}

::v-deep(.vs__actions) {
  align-items: center;
}

.form-actions {
  width: 100%;
  max-width: 500px;
  border-top: 1px solid #e5e5e5;
  padding: 0.75rem 0;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
}

.right-buttons {
  margin-top: 1em;
  display: flex;
  gap: 0.5rem;
}
</style>
